import { Controller } from "@hotwired/stimulus"

// Shared video editor controller for keyboard navigation and segment selection
// Works with any template type that uses the video_editor layout
export default class extends Controller {
  static targets = ["timeline", "segmentCard"]
  static values = {
    configId: Number,
    selectedIndex: { type: Number, default: 0 }
  }

  connect() {
    this.bindKeyboardNavigation()
    // Read server-rendered selected index for keyboard navigation
    this.syncSelectedIndexFromServer()
  }

  disconnect() {
    this.unbindKeyboardNavigation()
  }

  syncSelectedIndexFromServer() {
    // Read the selected index from the data attribute for keyboard nav only
    // Visual state is handled entirely by server-rendered classes
    const serverIndex = parseInt(this.element.dataset.videoEditorSelectedIndexValue, 10)
    if (!isNaN(serverIndex)) {
      this.selectedIndexValue = serverIndex
    }
  }

  bindKeyboardNavigation() {
    this.keydownHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.keydownHandler)
  }

  unbindKeyboardNavigation() {
    document.removeEventListener("keydown", this.keydownHandler)
  }

  handleKeydown(event) {
    // Don't handle if typing in an input
    if (event.target.matches("input, textarea, select")) return

    switch (event.key) {
      case "ArrowLeft":
      case "j":
        event.preventDefault()
        this.previousSegment()
        break
      case "ArrowRight":
      case "k":
        event.preventDefault()
        this.nextSegment()
        break
      case "Home":
        event.preventDefault()
        this.firstSegment()
        break
      case "End":
        event.preventDefault()
        this.lastSegment()
        break
    }
  }

  selectSegment(event) {
    const index = parseInt(event.params?.index ?? event.currentTarget.dataset.segmentIndex)
    if (!isNaN(index)) {
      this.selectedIndexValue = index
    }
  }

  previousSegment() {
    if (this.selectedIndexValue > 0) {
      this.navigateToSegment(this.selectedIndexValue - 1)
    }
  }

  nextSegment() {
    const totalSegments = this.segmentCardTargets.length || this.getSegmentCount()
    if (this.selectedIndexValue < totalSegments - 1) {
      this.navigateToSegment(this.selectedIndexValue + 1)
    }
  }

  firstSegment() {
    this.navigateToSegment(0)
  }

  lastSegment() {
    const totalSegments = this.segmentCardTargets.length || this.getSegmentCount()
    this.navigateToSegment(totalSegments - 1)
  }

  navigateToSegment(index) {
    // Find the segment card and click it
    const segmentCard = this.segmentCardTargets[index] ||
      this.element.querySelector(`[data-segment-index="${index}"]`)
    if (segmentCard) {
      segmentCard.click()
    }
  }

  getSegmentCount() {
    return this.element.querySelectorAll("[data-segment-index]").length
  }

  selectedIndexValueChanged() {
    // Scroll to selected segment (visual state is server-rendered)
    this.scrollToSelected()
  }

  scrollToSelected() {
    const selectedCard = this.segmentCardTargets[this.selectedIndexValue] ||
      this.element.querySelector(`[data-segment-index="${this.selectedIndexValue}"]`)
    if (selectedCard) {
      selectedCard.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    }
  }

  // Toggle detail editor panel for an item (Ken Burns, block settings, etc.)
  toggleDetailEditor(event) {
    event.preventDefault()
    event.stopPropagation()

    const itemIndex = event.currentTarget.dataset.itemIndex || event.currentTarget.dataset.imageIndex
    const editorPanel = this.element.querySelector(`[data-item-editor="${itemIndex}"], [data-image-editor="${itemIndex}"]`)
    const arrow = this.element.querySelector(`[data-editor-arrow="${itemIndex}"]`)

    if (editorPanel) {
      const isHidden = editorPanel.classList.contains("hidden")
      editorPanel.classList.toggle("hidden")

      // Rotate arrow
      if (arrow) {
        arrow.classList.toggle("rotate-180", isHidden)
      }
    }
  }

  // Alias for backwards compatibility
  toggleImageEditor(event) {
    this.toggleDetailEditor(event)
  }
}
